import axios from "axios";

// Use REACT_APP_BACKEND_URL, but when the site is served from the same domain
// (e.g. page on https://www.carza.co.in, API host https://carza.co.in), call the
// same origin instead — avoids cross-origin preflight redirects that browsers block.
const API = (() => {
  try {
    const url = new URL(process.env.REACT_APP_BACKEND_URL);
    const pageHost = window.location.hostname.replace(/^www\./, "");
    const apiHost = url.hostname.replace(/^www\./, "");
    if (pageHost && pageHost === apiHost) return `${window.location.origin}/api`;
  } catch (e) { /* fall through to env default */ }
  return `${process.env.REACT_APP_BACKEND_URL}/api`;
})();

export const api = axios.create({ baseURL: API, timeout: 15000 });

export function formatApiError(e, fallback = "Something went wrong. Please try again.") {
  const detail = e?.response?.data?.detail;
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  return String(detail);
}

export const submitBooking = (payload) => api.post("/bookings", payload).then((r) => r.data);
export const submitB2B = (payload) => api.post("/leads/b2b", payload).then((r) => r.data);
export const submitPartner = (payload) => api.post("/leads/partner", payload).then((r) => r.data);
export const submitInvestor = (payload) => api.post("/leads/investor", payload).then((r) => r.data);
export const fetchConfig = () => api.get("/config").then((r) => r.data.config);

export const createPaymentOrder = (bookingId) => api.post("/payments/create-order", { booking_id: bookingId }).then((r) => r.data);
export const verifyPayment = (payload) => api.post("/payments/verify", payload).then((r) => r.data);
export const paymentStatus = (bookingId) => api.get(`/payments/status?booking_id=${encodeURIComponent(bookingId)}`).then((r) => r.data);
export const paymentFailed = (bookingId, reason) => api.post("/payments/failed", { booking_id: bookingId, reason }).catch(() => {});

export function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
    document.body.appendChild(s);
  });
}

export const sendOtp = (phone, purpose) => api.post("/auth/otp/send", { phone, purpose }).then((r) => r.data);
export const verifyOtp = (phone, otp, purpose) => api.post("/auth/otp/verify", { phone, otp, purpose }).then((r) => r.data);
export const saveProfile = (token, profile) =>
  api.put("/auth/profile", profile, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

export const auth = (token) => ({ Authorization: `Bearer ${token}` });

export const adminApi = {
  login: (email, password) => api.post("/admin/login", { email, password }).then((r) => r.data),
  me: (token) => api.get("/admin/me", { headers: auth(token) }).then((r) => r.data),
  stats: (token) => api.get("/admin/stats", { headers: auth(token) }).then((r) => r.data),
  list: (kind, token) => api.get(`/admin/submissions/${kind}`, { headers: auth(token) }).then((r) => r.data.items),
  setStatus: (kind, id, status, token) =>
    api.patch(`/admin/submissions/${kind}/${id}`, { status }, { headers: auth(token) }).then((r) => r.data),
  saveConfig: (config, token) => api.put("/admin/config", { config }, { headers: auth(token) }).then((r) => r.data),
  notifications: (token) => api.get("/admin/notifications", { headers: auth(token) }).then((r) => r.data),
  saveNotifySettings: (patch, token) => api.put("/admin/notifications/settings", patch, { headers: auth(token) }).then((r) => r.data),
  testNotify: (channel, token) => api.post("/admin/notifications/test", { channel }, { headers: auth(token) }).then((r) => r.data),
  retryNotify: (token) => api.post("/admin/notifications/retry", {}, { headers: auth(token) }).then((r) => r.data),
  exportExcel: (token) => api.get("/admin/export/excel", { headers: auth(token), responseType: "blob", timeout: 60000 }).then((r) => r.data),
  partners: (token) => api.get("/admin/partners", { headers: auth(token) }).then((r) => r.data.partners),
  addPartner: (data, token) => api.post("/admin/partners", data, { headers: auth(token) }).then((r) => r.data),
  updatePartner: (pid, patch, token) => api.patch(`/admin/partners/${pid}`, patch, { headers: auth(token) }).then((r) => r.data),
  deletePartner: (pid, token) => api.delete(`/admin/partners/${pid}`, { headers: auth(token) }).then((r) => r.data),
  assignPartner: (bookingId, partnerId, token) =>
    api.post(`/admin/bookings/${bookingId}/assign`, { partner_id: partnerId }, { headers: auth(token) }).then((r) => r.data),
  jobStatus: (bookingId, status, token) =>
    api.post(`/admin/bookings/${bookingId}/job-status`, { status }, { headers: auth(token) }).then((r) => r.data),
};

export const partnerApi = {
  access: (phone, pin) => api.post("/partner/access", { phone, pin }).then((r) => r.data),
  setAvailability: (phone, pin, availability) => api.post("/partner/availability", { phone, pin, availability }).then((r) => r.data),
  jobAction: (phone, pin, action) => api.post("/partner/job-action", { phone, pin, action }).then((r) => r.data),
};

export const waLink = (num, text) =>
  `https://wa.me/${num}?text=${encodeURIComponent(text || "Hi CARZA, I'd like to know more about your car care services.")}`;

export const aggregatorApi = {
  centres: () => api.get("/aggregator/centres").then((r) => r.data),
  availability: (code) => api.get(`/aggregator/centres/${encodeURIComponent(code)}/availability`).then((r) => r.data),
  slots: (code, date) => api.get(`/aggregator/centres/${encodeURIComponent(code)}/slots?date=${encodeURIComponent(date)}`).then((r) => r.data),
  createBooking: (payload) => api.post("/aggregator/bookings", payload).then((r) => r.data),
  booking: (ref, phone) => api.get(`/aggregator/bookings/${encodeURIComponent(ref)}?phone=${encodeURIComponent(phone)}`).then((r) => r.data),
  cancel: (ref, payload) => api.post(`/aggregator/bookings/${encodeURIComponent(ref)}/cancel`, payload).then((r) => r.data),
  reschedule: (ref, payload) => api.post(`/aggregator/bookings/${encodeURIComponent(ref)}/reschedule`, payload).then((r) => r.data),
};

export const vendorApi = {
  login: (centre_code, pin) => api.post("/vendor/login", { centre_code, pin }).then((r) => r.data),
  me: (token) => api.get("/vendor/me", { headers: auth(token) }).then((r) => r.data),
  bookings: (token) => api.get("/vendor/bookings", { headers: auth(token) }).then((r) => r.data),
  action: (ref, action, token, body) =>
    api.post(`/vendor/bookings/${encodeURIComponent(ref)}/${action}`, body || {}, { headers: auth(token) }).then((r) => r.data),
  saveConfig: (cfg, token) => api.put("/vendor/config", cfg, { headers: auth(token) }).then((r) => r.data),
};
